import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Users,
    UserPlus,
    Search,
    MoreVertical,
    CheckCircle2,
    Clock,
    ArrowRight,
    Settings,
    Shield,
    Target,
    Calendar,
    AlertTriangle,
    Plus,
    Trash2,
    Info,
    UserCheck,
    Zap,
    Save,
    Filter,
    ChevronRight,
    X
} from 'lucide-react';
import './OnboardingSponsorship.css';
import { getAllUsersRequest } from '../../../redux/actions/userActions';
import { fetchOnboardingSponsorConfig, updateOnboardingSponsorConfig } from '../../../redux/actions/onboardingSponsorConfigActions';
import { fetchOnboardingConfig } from '../../../redux/actions/onboardingConfigActions';
import { API_BASE_URL } from '../../../../api';
import Swal from 'sweetalert2';

const OnboardingSponsorship = () => {
    const dispatch = useDispatch();

    const { users = [], loading = false } = useSelector(state => state.user || {});
    const { config: remoteConfig } = useSelector(state => state.onboardingSponsorConfig || { config: null });
    const { config: onboardingConfig } = useSelector(state => state.onboardingConfig || { config: null });

    const [view, setView] = useState('management');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [settingsTab, setSettingsTab] = useState('eligibility');

    const [localSettings, setLocalSettings] = useState({
        minSeniority: 6,
        maxRecruits: 2,
        cdiOnly: true,
        managerOrNot: false,
        assignmentMode: 'manual',
        matchSameDept: true,
        matchSameSite: true,
        matchSameJob: false,
        assignmentAlertDays: 7,
        durationMode: 'SAME_AS_ONBOARDING',
        customDurationValue: 3,
        customDurationUnit: 'MONTHS',
        missions: [
            { id: 1, title: "Appel de bienvenue avant J1", desc: "Appeler la recrue pour se présenter avant son arrivée." },
            { id: 2, title: "Déjeuner d'intégration", desc: "Organiser un déjeuner avec l'équipe durant la 1ère semaine." },
            { id: 3, title: "Présentation des outils internes", desc: "Vérifier que la recrue maîtrise les outils de base." }
        ],
        notifyOnSponsorLeave: true,
        allowIncompatibilityRequest: false
    });

    const [newMissionTitle, setNewMissionTitle] = useState('');
    const [newMissionDesc, setNewMissionDesc] = useState('');
    const [showAddMissionForm, setShowAddMissionForm] = useState(false);

    // Assignment Modal State
    const [selectedRecruit, setSelectedRecruit] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [modalSearch, setModalSearch] = useState('');

    // Auto Suggestion States
    const [suggestedBuddy, setSuggestedBuddy] = useState(null);
    const [suggestionRecruitId, setSuggestionRecruitId] = useState(null);
    const [ignoredSponsorIds, setIgnoredSponsorIds] = useState([]);
    const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(fetchOnboardingSponsorConfig());
        dispatch(fetchOnboardingConfig());
    }, [dispatch]);

    useEffect(() => {
        if (remoteConfig) {
            setLocalSettings({
                minSeniority: remoteConfig.minSeniority !== undefined ? remoteConfig.minSeniority : 6,
                maxRecruits: remoteConfig.maxRecruits !== undefined ? remoteConfig.maxRecruits : 2,
                cdiOnly: remoteConfig.cdiOnly !== undefined ? remoteConfig.cdiOnly : true,
                managerOrNot: remoteConfig.managerOrNot !== undefined ? remoteConfig.managerOrNot : false,
                assignmentMode: remoteConfig.assignmentMode || 'manual',
                matchSameDept: remoteConfig.matchSameDept !== undefined ? remoteConfig.matchSameDept : true,
                matchSameSite: remoteConfig.matchSameSite !== undefined ? remoteConfig.matchSameSite : true,
                matchSameJob: remoteConfig.matchSameJob !== undefined ? remoteConfig.matchSameJob : false,
                assignmentAlertDays: remoteConfig.assignmentAlertDays !== undefined ? remoteConfig.assignmentAlertDays : 7,
                durationMode: remoteConfig.durationMode || 'SAME_AS_ONBOARDING',
                customDurationValue: remoteConfig.customDurationValue !== undefined ? remoteConfig.customDurationValue : 3,
                customDurationUnit: remoteConfig.customDurationUnit || 'MONTHS',
                missions: Array.isArray(remoteConfig.missions) ? remoteConfig.missions : [
                    { id: 1, title: "Appel de bienvenue avant J1", desc: "Appeler la recrue pour se présenter avant son arrivée." },
                    { id: 2, title: "Déjeuner d'intégration", desc: "Organiser un déjeuner avec l'équipe durant la 1ère semaine." },
                    { id: 3, title: "Présentation des outils internes", desc: "Vérifier que la recrue maîtrise les outils de base." }
                ],
                notifyOnSponsorLeave: remoteConfig.notifyOnSponsorLeave !== undefined ? remoteConfig.notifyOnSponsorLeave : true,
                allowIncompatibilityRequest: remoteConfig.allowIncompatibilityRequest !== undefined ? remoteConfig.allowIncompatibilityRequest : false
            });
        }
    }, [remoteConfig]);

    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1);

    const recruits = users.filter(user => {
        if (!user.hireDate) return false;
        const hDate = new Date(user.hireDate);
        if (isNaN(hDate.getTime())) return false;
        const hTime = new Date(user.hireDate).setHours(0, 0, 0, 0);
        const startTime = new Date(oneMonthAgo).setHours(0, 0, 0, 0);
        const endTime = new Date(now).setHours(23, 59, 59, 999);
        if (hTime < startTime || hTime > endTime) return false;
        const hasBuddy = !!user.buddyId;
        if (filterStatus === 'unassigned' && hasBuddy) return false;
        if (filterStatus === 'assigned' && !hasBuddy) return false;
        if (searchTerm) {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
            const jobName = (user.jobName || '').toLowerCase();
            const buddyName = (user.buddyFullName || '').toLowerCase();
            const query = searchTerm.toLowerCase();
            return fullName.includes(query) || jobName.includes(query) || buddyName.includes(query);
        }
        return true;
    });

    const handleSettingChange = (field, value) => {
        setLocalSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleAddMission = () => {
        if (!newMissionTitle.trim()) return;
        setLocalSettings(prev => ({
            ...prev,
            missions: [...prev.missions, { id: Date.now(), title: newMissionTitle.trim(), desc: newMissionDesc.trim() }]
        }));
        setNewMissionTitle('');
        setNewMissionDesc('');
        setShowAddMissionForm(false);
    };

    const handleDeleteMission = (id) => {
        setLocalSettings(prev => ({ ...prev, missions: prev.missions.filter(m => m.id !== id) }));
    };

    const handleSaveConfig = () => {
        dispatch(updateOnboardingSponsorConfig(localSettings));
    };

    const handleOpenAssignModal = (recruit) => {
        setSelectedRecruit(recruit);
        setShowAssignModal(true);
        setModalSearch('');
    };

    const handleAssignBuddy = async (buddy) => {
        const currentFilleuls = users.filter(u => u.buddyId === buddy.id).length;
        if (currentFilleuls >= localSettings.maxRecruits) {
            Swal.fire({
                icon: 'warning',
                title: 'Limite de parrainage atteinte',
                text: `Ce parrain a déjà atteint le nombre maximum de filleuls autorisé par vos paramètres (${localSettings.maxRecruits}).`,
                confirmButtonColor: '#dc3545'
            });
            return;
        }

        try {
            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const response = await fetch(`${API_BASE_URL}/api/users/${selectedRecruit.id}/buddy?buddyId=${buddy.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'uid': uid
                }
            });
            
            if (!response.ok) throw new Error("Erreur");
            
            setShowAssignModal(false);
            setSelectedRecruit(null);
            
            Swal.fire({
                icon: 'success',
                title: 'Parrain assigné',
                text: `Le parrain ${buddy.fullName} a été affecté à la recrue.`,
                timer: 2000,
                showConfirmButton: false
            });
            
            dispatch(getAllUsersRequest());
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: "Une erreur est survenue lors de l'affectation du parrain."
            });
        }
    };

    const handleRemoveBuddy = async (recruitId) => {
        const result = await Swal.fire({
            title: 'Retirer le parrain ?',
            text: "Êtes-vous sûr de vouloir dissocier ce parrain de cette recrue ?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Oui, retirer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed) {
            try {
                const token = sessionStorage.getItem("token");
                const uid = sessionStorage.getItem("uid");
                const response = await fetch(`${API_BASE_URL}/api/users/${recruitId}/buddy`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'uid': uid
                    }
                });
                
                if (!response.ok) throw new Error("Erreur");
                
                Swal.fire({
                    icon: 'success',
                    title: 'Parrain retiré',
                    text: 'Le parrain a été retiré avec succès.',
                    timer: 2000,
                    showConfirmButton: false
                });
                
                dispatch(getAllUsersRequest());
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: "Impossible de retirer le parrain."
                });
            }
        }
    };

    const getEligibleSponsorCandidates = (recruit, excludedIds = []) => {
        return users.filter(u => {
            if (u.id === recruit.id) return false;
            if (excludedIds.includes(u.id)) return false;
            if (localSettings.cdiOnly && u.typeContrat !== 'CDI') return false;
            if (localSettings.managerOrNot) {
                const isManager = u.roles?.some(r => r.isManagerRole === true || r.name?.toLowerCase().includes('manager'));
                if (!isManager) return false;
            }
            if (u.hireDate) {
                const hire = new Date(u.hireDate);
                const now = new Date();
                const months = (now.getFullYear() - hire.getFullYear()) * 12 + (now.getMonth() - hire.getMonth());
                if (months < localSettings.minSeniority) return false;
            } else {
                return false;
            }
            return true;
        }).map(u => {
            let score = 0;
            const badges = [];
            if (u.entityId === recruit.entityId) {
                score += 3;
                badges.push({ type: 'entity', label: 'Même Entité' });
            }
            if (u.jobId === recruit.jobId) {
                score += 2;
                badges.push({ type: 'job', label: 'Même Poste' });
            }
            if (u.city && recruit.city && u.city.toLowerCase() === recruit.city.toLowerCase()) {
                score += 1;
                badges.push({ type: 'site', label: 'Même Site' });
            }
            const filleulsCount = users.filter(x => x.buddyId === u.id).length;
            return { ...u, score, badges, filleulsCount };
        }).sort((a, b) => b.score - a.score || a.filleulsCount - b.filleulsCount);
    };

    const handleSuggestBuddy = (recruit) => {
        setSuggestionRecruitId(recruit.id);
        setIgnoredSponsorIds([]);
        setIsGeneratingSuggestion(true);
        setSuggestedBuddy(null);

        setTimeout(() => {
            const candidates = getEligibleSponsorCandidates(recruit, []);
            const eligible = candidates.filter(c => c.filleulsCount < localSettings.maxRecruits);
            
            setIsGeneratingSuggestion(false);
            if (eligible.length > 0) {
                setSuggestedBuddy(eligible[0]);
            } else {
                setSuggestionRecruitId(null);
                Swal.fire({
                    icon: 'info',
                    title: 'Aucun parrain disponible',
                    text: "Aucun collaborateur ne répond aux critères d'éligibilité définis dans le paramétrage pour le moment.",
                    confirmButtonColor: '#dc3545'
                });
            }
        }, 550);
    };

    const handleNextSuggestion = (recruit) => {
        if (!suggestedBuddy) return;
        setIsGeneratingSuggestion(true);
        
        const nextIgnored = [...ignoredSponsorIds, suggestedBuddy.id];
        setIgnoredSponsorIds(nextIgnored);

        setTimeout(() => {
            const candidates = getEligibleSponsorCandidates(recruit, nextIgnored);
            const eligible = candidates.filter(c => c.filleulsCount < localSettings.maxRecruits);
            
            setIsGeneratingSuggestion(false);
            if (eligible.length > 0) {
                setSuggestedBuddy(eligible[0]);
            } else {
                setIgnoredSponsorIds([]);
                const resetCandidates = getEligibleSponsorCandidates(recruit, []);
                const resetEligible = resetCandidates.filter(c => c.filleulsCount < localSettings.maxRecruits);
                if (resetEligible.length > 0) {
                    setSuggestedBuddy(resetEligible[0]);
                    Swal.fire({
                        icon: 'info',
                        title: 'Fin de la liste',
                        text: "Vous avez parcouru tous les parrains éligibles. La recherche reprend au début.",
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    setSuggestedBuddy(null);
                    setSuggestionRecruitId(null);
                }
            }
        }, 450);
    };

    const handleConfirmSuggestion = async (recruitId, buddy) => {
        try {
            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const response = await fetch(`${API_BASE_URL}/api/users/${recruitId}/buddy?buddyId=${buddy.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'uid': uid
                }
            });
            
            if (!response.ok) throw new Error("Erreur");
            
            setSuggestedBuddy(null);
            setSuggestionRecruitId(null);
            setIgnoredSponsorIds([]);
            
            Swal.fire({
                icon: 'success',
                title: 'Parrain assigné',
                text: `Le parrain ${buddy.fullName} a été affecté avec succès.`,
                timer: 2000,
                showConfirmButton: false
            });
            
            dispatch(getAllUsersRequest());
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: "Une erreur est survenue lors de l'affectation."
            });
        }
    };

    const handleCancelSuggestion = () => {
        setSuggestedBuddy(null);
        setSuggestionRecruitId(null);
        setIgnoredSponsorIds([]);
    };

    const formatDate = (dateInput) => {
        if (!dateInput) return 'N/A';
        try {
            const d = new Date(dateInput);
            if (isNaN(d.getTime())) return String(dateInput);
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch { return String(dateInput); }
    };

    const getInitials = (firstName, lastName) => {
        return (((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase()) || 'U';
    };

    const assignedCount = users.filter(u => {
        if (!u.hireDate) return false;
        const t = new Date(u.hireDate).setHours(0,0,0,0);
        return t >= new Date(oneMonthAgo).setHours(0,0,0,0) && t <= new Date(now).setHours(23,59,59,999) && !!u.buddyId;
    }).length;

    const totalRecruitsCount = users.filter(u => {
        if (!u.hireDate) return false;
        const t = new Date(u.hireDate).setHours(0,0,0,0);
        return t >= new Date(oneMonthAgo).setHours(0,0,0,0) && t <= new Date(now).setHours(23,59,59,999);
    }).length;

    const settingsTabs = [
        { id: 'eligibility', label: '1. Éligibilité & Filtres', icon: <UserCheck size={14} /> },
        { id: 'assignment', label: '2. Affectation Parrains', icon: <Zap size={14} /> },
        { id: 'duration', label: '3. Durée & Cycles', icon: <Calendar size={14} /> },
        { id: 'missions', label: '4. Missions Obligatoires', icon: <Target size={14} /> },
        { id: 'exceptions', label: '5. Gestion des Exceptions', icon: <AlertTriangle size={14} /> }
    ];

    return (
        <div className="oc-page">

            {/* ── Header ── */}
            <div className="oc-header">
                <div className="oc-header-left">
                    <h1>Programme de Parrainage (Buddy)</h1>
                    <p className="oc-subtitle">Gérez les binômes et configurez les règles d'accompagnement.</p>
                </div>
                <div className="oc-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* View Switcher */}
                    <div className="os-view-switcher">
                        <button
                            className={`os-view-btn ${view === 'management' ? 'active' : ''}`}
                            onClick={() => setView('management')}
                        >
                            <Users size={14} /> Suivi
                        </button>
                        <button
                            className={`os-view-btn ${view === 'settings' ? 'active' : ''}`}
                            onClick={() => setView('settings')}
                        >
                            <Settings size={14} /> Paramétrage
                        </button>
                    </div>

                    {view === 'settings' && (
                        <button className="oc-btn oc-btn-primary" onClick={handleSaveConfig}>
                            <Save size={13} />
                            <span>Enregistrer la configuration</span>
                        </button>
                    )}
                    {/* Le bouton Nouveau Buddy a été retiré selon vos instructions */}
                </div>
            </div>

            {/* ── Info Bar ── */}
            {view === 'management' && (
                <div className="oc-info-bar">
                    <div className="oc-info-icon"><Calendar size={14} /></div>
                    <div className="oc-info-text">
                        Affichage des recrues embauchées entre le <strong>{formatDate(oneMonthAgo)}</strong> et le <strong>{formatDate(now)}</strong>.
                        &nbsp;—&nbsp; <strong>{assignedCount}/{totalRecruitsCount}</strong> recrues ont un parrain assigné.
                    </div>
                </div>
            )}
            {view === 'settings' && (
                <div className="oc-info-bar">
                    <div className="oc-info-icon"><Info size={14} /></div>
                    <div className="oc-info-text">
                        Modifiez les paramètres ci-dessous librement. Cliquez sur <strong>Enregistrer la configuration</strong> pour valider vos modifications.
                    </div>
                </div>
            )}

            {/* ══════════════════════ MANAGEMENT VIEW ══════════════════════ */}
            {view === 'management' && (
                <div className="fade-in" style={{ padding: '16px 24px' }}>

                    {/* Toolbar */}
                    <div className="os-toolbar">
                        <div className="os-search-wrapper">
                            <Search size={14} className="os-search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher une recrue ou un parrain..."
                                className="os-search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="os-filter-tabs">
                            {[
                                { key: 'all', label: 'Tous' },
                                { key: 'unassigned', label: 'Sans parrain' },
                                { key: 'assigned', label: 'Accompagnés' }
                            ].map(f => (
                                <button
                                    key={f.key}
                                    className={`os-filter-tab ${filterStatus === f.key ? 'active' : ''}`}
                                    onClick={() => setFilterStatus(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="os-table-container">
                        {loading ? (
                            <div style={{ padding: '48px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                <div className="oc-spinner" />
                                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 600 }}>Chargement des collaborateurs...</span>
                            </div>
                        ) : recruits.length === 0 ? (
                            <div className="oc-empty" style={{ padding: '64px 16px' }}>
                                <div className="oc-empty-icon"><Users size={22} /></div>
                                <p className="oc-empty-title">Aucune recrue trouvée</p>
                                <p className="oc-empty-desc">
                                    Aucun collaborateur n'a sa date d'embauche comprise entre le {formatDate(oneMonthAgo)} et le {formatDate(now)}.
                                </p>
                            </div>
                        ) : (
                            <table className="os-table">
                                <thead>
                                <tr>
                                    <th>Nouvelle Recrue</th>
                                    <th>Poste</th>
                                    <th>Entité</th>
                                    <th>Date d'entrée</th>
                                    <th>Manager</th>
                                    <th>Parrain Assigné</th>
                                </tr>
                                </thead>
                                <tbody>
                                {recruits.map((recruit) => (
                                    <tr key={recruit.id}>
                                        <td>
                                            <div className="os-user-cell">
                                                <div className="os-avatar">{getInitials(recruit.firstName, recruit.lastName)}</div>
                                                <div>
                                                    <div className="os-user-name">{recruit.firstName} {recruit.lastName}</div>
                                                    <div className="os-user-sub" style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600 }}>
                                                        Matricule: {recruit.employeeCode || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#374151' }}>
                                                {recruit.jobName || 'Aucun poste'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="oc-badge-entity" style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '12px', background: '#eff6ff', color: '#1e40af', fontWeight: 600 }}>
                                                {recruit.entityName || 'Aucune entité'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="os-date-cell">
                                                <Clock size={13} />
                                                <span>{formatDate(recruit.hireDate)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '11px', color: '#4b5563', fontWeight: 500 }}>
                                                {recruit.managerFullName || 'Aucun manager'}
                                            </span>
                                        </td>
                                        <td>
                                            {recruit.buddyId ? (
                                                <div className="os-buddy-cell" style={{ position: 'relative' }}>
                                                    <div className="os-buddy-avatar">{getInitials(recruit.buddyFullName, '')}</div>
                                                    <div>
                                                        <div className="os-buddy-name">{recruit.buddyFullName}</div>
                                                        <div className="os-buddy-email">{recruit.buddyEmail || '—'}</div>
                                                    </div>
                                                    {localSettings.assignmentMode !== 'auto' && (
                                                        <button 
                                                            className="os-btn-remove-buddy" 
                                                            onClick={() => handleRemoveBuddy(recruit.id)}
                                                            title="Retirer le parrain"
                                                            type="button"
                                                        >
                                                            <X size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : suggestionRecruitId === recruit.id ? (
                                                isGeneratingSuggestion ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f5f3ff', borderRadius: '8px', border: '1px dashed #c084fc', height: '42px', minWidth: '180px' }}>
                                                        <div className="oc-spinner" style={{ width: '12px', height: '12px', borderColor: '#707ea7 transparent transparent transparent', animation: 'os-spin 0.6s linear infinite' }} />
                                                        <span style={{ fontSize: '10.5px', color: '#707ea7', fontWeight: 600 }}>Calcul du meilleur match...</span>
                                                    </div>
                                                ) : suggestedBuddy ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: '#faf5ff', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e9d5ff', minWidth: '220px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                                                            <span style={{ fontSize: '9px', fontWeight: 800, color: '#707ea7', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                🪄 Suggestion
                                                            </span>
                                                            <div style={{ display: 'flex', gap: '3px' }}>
                                                                <button 
                                                                    onClick={() => handleConfirmSuggestion(recruit.id, suggestedBuddy)} 
                                                                    style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '9px', fontWeight: 700, cursor: 'pointer' }}
                                                                    title="Valider"
                                                                >
                                                                    ✓ Valider
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleNextSuggestion(recruit)} 
                                                                    style={{ background: '#707ea7', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 5px', fontSize: '9px', fontWeight: 700, cursor: 'pointer' }}
                                                                    title="Suggérer un autre"
                                                                >
                                                                    🔄 Relancer
                                                                </button>
                                                                <button 
                                                                    onClick={handleCancelSuggestion} 
                                                                    style={{ background: '#9ca3af', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 4px', fontSize: '9px', fontWeight: 700, cursor: 'pointer' }}
                                                                    title="Annuler"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#c084fc', color: 'white', display: 'flex', alignItems: 'center', fontSize: '9px', fontWeight: 700, flexShrink: 0, justifyContent: 'center' }}>
                                                                {getInitials(suggestedBuddy.firstName, suggestedBuddy.lastName)}
                                                            </div>
                                                            <div style={{ overflow: 'hidden' }}>
                                                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    {suggestedBuddy.firstName} {suggestedBuddy.lastName}
                                                                </div>
                                                                <div style={{ fontSize: '9px', color: '#6b7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    {suggestedBuddy.jobName || 'Sans poste'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {suggestedBuddy.badges.length > 0 && (
                                                            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '2px' }}>
                                                                {suggestedBuddy.badges.map(b => (
                                                                    <span key={b.type} style={{ fontSize: '8px', padding: '1px 4px', borderRadius: '4px', background: b.type === 'entity' ? '#ecfdf5' : b.type === 'job' ? '#eff6ff' : '#f9fefd', color: b.type === 'entity' ? '#065f46' : b.type === 'job' ? '#1e40af' : '#455584', fontWeight: 700 }}>
                                                                        {b.label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : null
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <button 
                                                        className={`os-btn-assign ${localSettings.assignmentMode === 'auto' ? 'disabled' : ''}`}
                                                        disabled={localSettings.assignmentMode === 'auto'}
                                                        onClick={() => handleOpenAssignModal(recruit)}
                                                        type="button"
                                                    >
                                                        {localSettings.assignmentMode === 'auto' ? 'Auto-assignation' : '+ Affecter'}
                                                    </button>
                                                    {localSettings.assignmentMode !== 'auto' && (
                                                        <button 
                                                            className="os-btn-suggest"
                                                            onClick={() => handleSuggestBuddy(recruit)}
                                                            title="Suggestion magique par IA / Critères de matching"
                                                            type="button"
                                                            style={{
                                                                background: 'linear-gradient(135deg, #707ea7 0%, #707ea7 100%)',
                                                                color: 'white',
                                                                border: 'none',
                                                                padding: '6px 10px',
                                                                borderRadius: '6px',
                                                                fontSize: '11.5px',
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                                                                transition: 'all 0.2s',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            🪄 Suggérer
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* ── SPONSOR SUGGESTION MODAL ── */}
                    {showAssignModal && selectedRecruit && (
                        <div className="os-modal-overlay" onClick={() => setShowAssignModal(false)}>
                            <div className="os-modal-container" onClick={(e) => e.stopPropagation()}>
                                <div className="os-modal-header">
                                    <span className="os-modal-title">Affecter un Parrain</span>
                                    <button className="os-modal-close" onClick={() => setShowAssignModal(false)}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="os-modal-body">
                                    <div className="os-modal-recruit-summary">
                                        Nouvelle recrue : <strong>{selectedRecruit.firstName} {selectedRecruit.lastName}</strong> ({selectedRecruit.jobName || 'Aucun poste'} — {selectedRecruit.entityName || 'Aucune entité'})
                                    </div>
                                    <div className="os-search-wrapper" style={{ marginBottom: '12px' }}>
                                        <Search size={14} className="os-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher un parrain par nom ou poste..."
                                            className="os-search-input"
                                            value={modalSearch}
                                            onChange={(e) => setModalSearch(e.target.value)}
                                        />
                                    </div>
                                    
                                    <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #f1f5f9', paddingBottom: '6px' }}>
                                        Candidats éligibles triés par pertinence :
                                    </div>

                                    <div className="os-candidate-list">
                                        {users.filter(u => {
                                            if (selectedRecruit && u.id === selectedRecruit.id) return false;
                                            if (localSettings.cdiOnly && u.typeContrat !== 'CDI') return false;
                                            if (localSettings.managerOrNot) {
                                                const isManager = u.roles?.some(r => r.isManagerRole === true || r.name?.toLowerCase().includes('manager'));
                                                if (!isManager) return false;
                                            }
                                            if (u.hireDate) {
                                                const hire = new Date(u.hireDate);
                                                const now = new Date();
                                                const months = (now.getFullYear() - hire.getFullYear()) * 12 + (now.getMonth() - hire.getMonth());
                                                if (months < localSettings.minSeniority) return false;
                                            } else {
                                                return false;
                                            }
                                            if (modalSearch) {
                                                const q = modalSearch.toLowerCase();
                                                const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                                                return fullName.includes(q) || (u.jobName || '').toLowerCase().includes(q);
                                            }
                                            return true;
                                        }).map(u => {
                                            let score = 0;
                                            const badges = [];
                                            if (u.entityId === selectedRecruit.entityId) {
                                                score += 3;
                                                badges.push({ type: 'entity', label: 'Même Entité' });
                                            }
                                            if (u.jobId === selectedRecruit.jobId) {
                                                score += 2;
                                                badges.push({ type: 'job', label: 'Même Poste' });
                                            }
                                            if (u.city && selectedRecruit.city && u.city.toLowerCase() === selectedRecruit.city.toLowerCase()) {
                                                score += 1;
                                                badges.push({ type: 'site', label: 'Même Site' });
                                            }
                                            const filleulsCount = users.filter(x => x.buddyId === u.id).length;
                                            return { ...u, score, badges, filleulsCount };
                                        }).sort((a, b) => b.score - a.score || a.filleulsCount - b.filleulsCount).length === 0 ? (
                                            <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '11px', fontWeight: 600 }}>
                                                Aucun parrain éligible ne correspond aux critères définis.
                                            </div>
                                        ) : (
                                            users.filter(u => {
                                                if (selectedRecruit && u.id === selectedRecruit.id) return false;
                                                if (localSettings.cdiOnly && u.typeContrat !== 'CDI') return false;
                                                if (localSettings.managerOrNot) {
                                                    const isManager = u.roles?.some(r => r.isManagerRole === true || r.name?.toLowerCase().includes('manager'));
                                                    if (!isManager) return false;
                                                }
                                                if (u.hireDate) {
                                                    const hire = new Date(u.hireDate);
                                                    const now = new Date();
                                                    const months = (now.getFullYear() - hire.getFullYear()) * 12 + (now.getMonth() - hire.getMonth());
                                                    if (months < localSettings.minSeniority) return false;
                                                } else {
                                                    return false;
                                                }
                                                if (modalSearch) {
                                                    const q = modalSearch.toLowerCase();
                                                    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                                                    return fullName.includes(q) || (u.jobName || '').toLowerCase().includes(q);
                                                }
                                                return true;
                                            }).map(u => {
                                                let score = 0;
                                                const badges = [];
                                                if (u.entityId === selectedRecruit.entityId) {
                                                    score += 3;
                                                    badges.push({ type: 'entity', label: 'Même Entité' });
                                                }
                                                if (u.jobId === selectedRecruit.jobId) {
                                                    score += 2;
                                                    badges.push({ type: 'job', label: 'Même Poste' });
                                                }
                                                if (u.city && selectedRecruit.city && u.city.toLowerCase() === selectedRecruit.city.toLowerCase()) {
                                                    score += 1;
                                                    badges.push({ type: 'site', label: 'Même Site' });
                                                }
                                                const filleulsCount = users.filter(x => x.buddyId === u.id).length;
                                                return { ...u, score, badges, filleulsCount };
                                            }).sort((a, b) => b.score - a.score || a.filleulsCount - b.filleulsCount).map(candidate => {
                                                const isLimitReached = candidate.filleulsCount >= localSettings.maxRecruits;
                                                return (
                                                    <div 
                                                        key={candidate.id} 
                                                        className={`os-candidate-row ${isLimitReached ? 'limit-reached' : ''}`}
                                                        onClick={() => !isLimitReached && handleAssignBuddy(candidate)}
                                                    >
                                                        <div className="os-candidate-info">
                                                            <div className="os-candidate-avatar">{getInitials(candidate.firstName, candidate.lastName)}</div>
                                                            <div>
                                                                <div className="os-candidate-name">{candidate.firstName} {candidate.lastName}</div>
                                                                <div className="os-candidate-sub">
                                                                    {candidate.jobName || 'Sans poste'} • Ancienneté: {candidate.seniority || 'N/A'}
                                                                </div>
                                                                {candidate.badges.length > 0 && (
                                                                    <div className="os-candidate-badges">
                                                                        {candidate.badges.map(b => (
                                                                            <span key={b.type} className={`oc-badge-match ${b.type}`}>{b.label}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className={`oc-badge-filleuls ${isLimitReached ? 'warning' : 'ok'}`}>
                                                                {candidate.filleulsCount}/{localSettings.maxRecruits} filleuls
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
</div>
)}



            {view === 'settings' && (
                <div className="oc-layout-split fade-in">

                    {/* Sidebar navigation */}
                    <div className="oc-sidebar-nav">
                        <span style={{
                            fontSize: '9px', fontWeight: 800, color: 'var(--oc-text-muted)',
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                            padding: '2px 14px 6px 14px', borderBottom: '1px solid #f3f4f6',
                            marginBottom: '6px', display: 'block'
                        }}>
                            PARAMÉTRAGE BUDDY
                        </span>

                        {settingsTabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`oc-tab-vertical ${settingsTab === tab.id ? 'active' : ''}`}
                                onClick={() => setSettingsTab(tab.id)}
                            >
                                <div className="oc-tab-vertical-border" />
                                <div className="oc-tab-vertical-icon">{tab.icon}</div>
                                <span>{tab.label}</span>
                            </button>
                        ))}

                        <div className="oc-sidebar-footer-info">
                            <div style={{ display: 'flex', gap: '6px', color: 'var(--oc-primary)', marginBottom: '2px', alignItems: 'center' }}>
                                <Info size={12} style={{ flexShrink: 0 }} />
                                <span style={{ fontSize: '10px', fontWeight: 700 }}>Buddy System</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '9px', color: '#6b7280', lineHeight: 1.3, fontWeight: 500 }}>
                                Les paramètres modifiés s'appliquent automatiquement à chaque nouvelle recrue dès leur enregistrement.
                            </p>
                        </div>
                    </div>

                    {/* Content panel */}
                    <div className="oc-content-panel">

                        {/* 1. ÉLIGIBILITÉ */}
                        {settingsTab === 'eligibility' && (
                            <div className="oc-config-grid fade-in">
                                {/* Left card: Ancienneté & charge */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><UserCheck size={12} /></div>
                                        <span>Critères d'ancienneté & charge</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Définissez le temps de présence minimum et le nombre maximum de recrues qu'un parrain peut accompagner simultanément.
                                        </p>

                                        <div className="oc-mini-field" style={{ marginBottom: '12px' }}>
                                            <span className="oc-mini-label">Ancienneté minimale</span>
                                            <div className="oc-mini-input-group" style={{ maxWidth: '160px' }}>
                                                <input
                                                    type="number"
                                                    value={localSettings.minSeniority}
                                                    onChange={(e) => handleSettingChange('minSeniority', parseInt(e.target.value) || 0)}
                                                    className="oc-mini-input"
                                                />
                                                <div className="oc-mini-input-tag">mois</div>
                                            </div>
                                            <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>
                                                Temps de présence requis en entreprise pour parrainer.
                                            </p>
                                        </div>

                                        <div className="oc-mini-field">
                                            <span className="oc-mini-label">Limite de filleuls simultanés</span>
                                            <div className="oc-mini-input-group" style={{ maxWidth: '160px' }}>
                                                <input
                                                    type="number"
                                                    value={localSettings.maxRecruits}
                                                    onChange={(e) => handleSettingChange('maxRecruits', parseInt(e.target.value) || 0)}
                                                    className="oc-mini-input"
                                                />
                                                <div className="oc-mini-input-tag">recrues</div>
                                            </div>
                                            <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>
                                                Maximum de recrues accompagnées en même temps par parrain.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Right card: Profil autorisé */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Shield size={12} /></div>
                                        <span>Profil autorisé comme parrain</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Restreignez le rôle de parrain selon le type de contrat et le statut managérial du collaborateur.
                                        </p>

                                        <div className="oc-source-block is-active" style={{ marginBottom: '10px' }}>
                                            <div className="oc-source-header">
                                                <div className="oc-source-icon manager">
                                                    <Shield size={12} />
                                                </div>
                                                <div className="oc-source-title">
                                                    <span className="oc-source-name">Contrats CDI uniquement</span>
                                                    <span className="oc-source-desc">Exclure les stagiaires, alternants et CDD du rôle de parrain.</span>
                                                </div>
                                            </div>
                                            <div className="oc-source-fields" style={{ paddingTop: '10px' }}>
                                                <div className="oc-toggle-row">
                                                    <span className="oc-toggle-label">Activer le filtre CDI</span>
                                                    <button
                                                        className={`oc-source-toggle accent ${localSettings.cdiOnly ? 'on' : ''}`}
                                                        onClick={() => handleSettingChange('cdiOnly', !localSettings.cdiOnly)}
                                                        type="button"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="oc-source-block is-active">
                                            <div className="oc-source-header">
                                                <div className="oc-source-icon collab">
                                                    <UserCheck size={12} />
                                                </div>
                                                <div className="oc-source-title">
                                                    <span className="oc-source-name">Restriction aux managers</span>
                                                    <span className="oc-source-desc">Autoriser uniquement les collaborateurs managers à jouer le rôle de parrain.</span>
                                                </div>
                                            </div>
                                            <div className="oc-source-fields" style={{ paddingTop: '10px' }}>
                                                <div className="oc-toggle-row">
                                                    <span className="oc-toggle-label">Managers seulement</span>
                                                    <button
                                                        className={`oc-source-toggle accent ${localSettings.managerOrNot ? 'on' : ''}`}
                                                        onClick={() => handleSettingChange('managerOrNot', !localSettings.managerOrNot)}
                                                        type="button"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 2. AFFECTATION */}
                        {settingsTab === 'assignment' && (
                            <div className="oc-config-grid fade-in">
                                {/* Left card: Mode d'affectation */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Zap size={12} /></div>
                                        <span>Mode d'affectation par défaut</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Choisissez comment le système propose ou assigne automatiquement un parrain à chaque nouvelle recrue.
                                        </p>

                                        <div className="oc-formula-options">
                                            {[
                                                { value: 'manual', label: 'Manuelle par les RH', hint: 'Recommandé — L\'équipe RH choisit le parrain manuellement.' },
                                                { value: 'auto', label: 'Automatique (Matching instantané)', hint: 'Le système assigne directement selon les critères configurés.' },
                                                { value: 'hybrid', label: 'Hybride (Suggestion + validation)', hint: 'Le système propose un candidat, validé ensuite par les RH.' },
                                            ].map(opt => (
                                                <div
                                                    key={opt.value}
                                                    className={`oc-formula-option ${localSettings.assignmentMode === opt.value ? 'selected' : ''}`}
                                                    onClick={() => handleSettingChange('assignmentMode', opt.value)}
                                                >
                                                    <div className="oc-formula-radio">
                                                        <div className="oc-formula-radio-dot" />
                                                    </div>
                                                    <div className="oc-formula-text">
                                                        <span className="oc-formula-name">{opt.label}</span>
                                                        <span className="oc-formula-hint">{opt.hint}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right card: Critères matching + alerte */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Filter size={12} /></div>
                                        <span>Critères de matching & alertes</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Activez les critères pris en compte pour le matching automatique ou la suggestion de parrain.
                                        </p>

                                        <div className="oc-source-block is-active" style={{ marginBottom: '12px' }}>
                                            <div className="oc-source-header">
                                                <div className="oc-source-icon manager"><Target size={12} /></div>
                                                <div className="oc-source-title">
                                                    <span className="oc-source-name">Critères de correspondance</span>
                                                    <span className="oc-source-desc">Paramètres utilisés lors du matching parrain / recrue.</span>
                                                </div>
                                            </div>
                                            <div className="oc-source-fields" style={{ paddingTop: '10px' }}>
                                                <div className="oc-toggle-grid">
                                                    {[
                                                        { key: 'matchSameDept', label: 'Même département / équipe' },
                                                        { key: 'matchSameSite', label: 'Même site géographique' },
                                                        { key: 'matchSameJob', label: 'Même métier / filière' },
                                                    ].map(c => (
                                                        <div key={c.key} className="oc-toggle-row">
                                                            <span className="oc-toggle-label">{c.label}</span>
                                                            <button
                                                                className={`oc-source-toggle accent ${localSettings[c.key] ? 'on' : ''}`}
                                                                onClick={() => handleSettingChange(c.key, !localSettings[c.key])}
                                                                type="button"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="oc-anon-option on">
                                            <div className="oc-anon-icon"><Clock size={14} /></div>
                                            <div className="oc-anon-text">
                                                <span className="oc-anon-title">Alerte de délai d'affectation</span>
                                                <span className="oc-anon-desc">Alerter les RH si aucun parrain n'est assigné avant :</span>
                                            </div>
                                            <div style={{ width: '85px', flexShrink: 0 }}>
                                                <div className="oc-mini-input-group">
                                                    <input
                                                        type="number"
                                                        value={localSettings.assignmentAlertDays}
                                                        onChange={(e) => handleSettingChange('assignmentAlertDays', parseInt(e.target.value) || 0)}
                                                        className="oc-mini-input"
                                                    />
                                                    <div className="oc-mini-input-tag" style={{ padding: '0 4px', fontSize: '9px' }}>J</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 3. DURÉE */}
                        {settingsTab === 'duration' && (
                            <div className="oc-config-grid fade-in">
                                {/* Left card: Mode de durée */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Calendar size={12} /></div>
                                        <span>Mode de calcul de la durée</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Choisissez comment le système définit automatiquement la durée de la période d'accompagnement parrain.
                                        </p>
                                        <div className="oc-formula-options">
                                            {[
                                                { value: 'SAME_AS_ONBOARDING', label: 'Même durée que l\'Onboarding', hint: 'L\'accompagnement s\'aligne automatiquement sur la durée du parcours ou du poste.' },
                                                { value: 'CUSTOM', label: 'Durée personnalisée spécifique', hint: 'Fixez un délai indépendant et spécifique pour les parrains.' },
                                            ].map(opt => (
                                                <div
                                                    key={opt.value}
                                                    className={`oc-formula-option ${localSettings.durationMode === opt.value ? 'selected' : ''}`}
                                                    onClick={() => handleSettingChange('durationMode', opt.value)}
                                                >
                                                    <div className="oc-formula-radio">
                                                        <div className="oc-formula-radio-dot" />
                                                    </div>
                                                    <div className="oc-formula-text">
                                                        <span className="oc-formula-name">{opt.label}</span>
                                                        <span className="oc-formula-hint">{opt.hint}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right card: Valeur personnalisée */}
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Clock size={12} /></div>
                                        <span>Délai applicable</span>
                                    </div>
                                    <div className="oc-card-body">
                                        {localSettings.durationMode === 'SAME_AS_ONBOARDING' ? (
                                            <div className="oc-anon-option on fade-in">
                                                <div className="oc-anon-icon"><CheckCircle2 size={14} /></div>
                                                <div className="oc-anon-text">
                                                    <span className="oc-anon-title">Aligné sur l'Onboarding</span>
                                                    <span className="oc-anon-desc">
                                                        La durée du parrainage est actuellement alignée sur la configuration générale de l'onboarding : <strong>{onboardingConfig?.defaultDuration || 30} jours</strong>.
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="fade-in">
                                                <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 14px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                                    Définissez la durée fixe durant laquelle le parrain suit activement la recrue, indépendamment du parcours d'intégration.
                                                </p>
                                                <div className="oc-mini-field">
                                                    <span className="oc-mini-label">Durée de l'accompagnement parrain</span>
                                                    <div className="oc-mini-input-group" style={{ maxWidth: '200px' }}>
                                                        <input
                                                            type="number"
                                                            value={localSettings.customDurationValue}
                                                            onChange={(e) => handleSettingChange('customDurationValue', parseInt(e.target.value) || 0)}
                                                            className="oc-mini-input"
                                                            style={{ width: '60px' }}
                                                        />
                                                        <select
                                                            value={localSettings.customDurationUnit}
                                                            onChange={(e) => handleSettingChange('customDurationUnit', e.target.value)}
                                                            className="oc-mini-input"
                                                            style={{ fontSize: '11px', fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer', paddingRight: '6px' }}
                                                        >
                                                            <option value="MONTHS">Mois</option>
                                                            <option value="WEEKS">Semaines</option>
                                                        </select>
                                                    </div>
                                                    <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#9ca3af', fontWeight: 500 }}>
                                                        Période standard d'accompagnement actif par le parrain.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. MISSIONS */}
                        {settingsTab === 'missions' && (
                            <div className="fade-in">
                                <div className="oc-card">
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Target size={12} /></div>
                                        <span>Missions & Jalons obligatoires</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: 1.5, fontWeight: 500 }}>
                                            Ajoutez, modifiez ou supprimez les tâches obligatoires que le parrain doit accomplir pour guider la recrue tout au long de son intégration.
                                        </p>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                            {localSettings.missions.map((mission) => (
                                                <div key={mission.id} className="oc-source-block is-active" style={{ padding: '10px 12px' }}>
                                                    <div className="oc-source-header" style={{ gap: '10px' }}>
                                                        <div className="oc-source-icon collab">
                                                            <CheckCircle2 size={12} />
                                                        </div>
                                                        <div className="oc-source-title">
                                                            <span className="oc-source-name">{mission.title}</span>
                                                            <span className="oc-source-desc">{mission.desc}</span>
                                                        </div>
                                                        <button
                                                            className="oc-btn oc-btn-danger"
                                                            style={{ padding: '3px 8px', flexShrink: 0 }}
                                                            onClick={() => handleDeleteMission(mission.id)}
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 size={11} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {showAddMissionForm ? (
                                            <div style={{ border: '1px dashed var(--oc-border)', padding: '12px', borderRadius: '8px', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '8px' }} className="fade-in">
                                                <input
                                                    type="text"
                                                    placeholder="Titre de la mission..."
                                                    value={newMissionTitle}
                                                    onChange={(e) => setNewMissionTitle(e.target.value)}
                                                    className="oc-input-text"
                                                    style={{ fontSize: '12px' }}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Description abrégée..."
                                                    value={newMissionDesc}
                                                    onChange={(e) => setNewMissionDesc(e.target.value)}
                                                    className="oc-input-text"
                                                    style={{ fontSize: '11px' }}
                                                />
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    <button className="oc-btn oc-btn-ghost" onClick={() => setShowAddMissionForm(false)}>
                                                        Annuler
                                                    </button>
                                                    <button className="oc-btn oc-btn-primary" onClick={handleAddMission}>
                                                        <Plus size={12} /> Ajouter
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                className="oc-btn oc-btn-ghost"
                                                style={{ width: '100%', justifyContent: 'center', borderStyle: 'dashed' }}
                                                onClick={() => setShowAddMissionForm(true)}
                                            >
                                                <Plus size={13} /> Ajouter une mission type
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 5. EXCEPTIONS */}
                        {settingsTab === 'exceptions' && (
                            <div className="oc-alerts-layout fade-in">
                                {/* Left: exceptions toggles */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {[
                                        {
                                            key: 'notifyOnSponsorLeave',
                                            title: 'Départ du parrain',
                                            desc: 'Alerter le service RH et suggérer un parrain remplaçant si le parrain quitte la société.',
                                            icon: <AlertTriangle size={12} />,
                                            iconClass: 'manager'
                                        },
                                        {
                                            key: 'allowIncompatibilityRequest',
                                            title: 'Procédure d\'incompatibilité',
                                            desc: 'Autoriser les recrues à faire une demande anonyme de changement de parrain.',
                                            icon: <Shield size={12} />,
                                            iconClass: 'collab'
                                        }
                                    ].map(item => (
                                        <div key={item.key} className="oc-source-block is-active">
                                            <div className="oc-source-header">
                                                <div className={`oc-source-icon ${item.iconClass}`}>{item.icon}</div>
                                                <div className="oc-source-title">
                                                    <span className="oc-source-name">{item.title}</span>
                                                    <span className="oc-source-desc">{item.desc}</span>
                                                </div>
                                            </div>
                                            <div className="oc-source-fields" style={{ paddingTop: '10px' }}>
                                                <div className="oc-toggle-row">
                                                    <span className="oc-toggle-label">Activer</span>
                                                    <button
                                                        className={`oc-source-toggle accent ${localSettings[item.key] ? 'on' : ''}`}
                                                        onClick={() => handleSettingChange(item.key, !localSettings[item.key])}
                                                        type="button"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Right: info card */}
                                <div className="oc-card" style={{ alignSelf: 'start' }}>
                                    <div className="oc-card-header">
                                        <div className="oc-card-header-icon"><Info size={12} /></div>
                                        <span>Congé longue durée</span>
                                    </div>
                                    <div className="oc-card-body">
                                        <div className="oc-anon-option on">
                                            <div className="oc-anon-icon"><Clock size={14} /></div>
                                            <div className="oc-anon-text">
                                                <span className="oc-anon-title">Mise en pause automatique</span>
                                                <span className="oc-anon-desc">
                                                    En cas de congé longue durée, le parrainage est mis en «&nbsp;Pause&nbsp;» et le manager est notifié pour prendre le relais si nécessaire.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            <style>{`
                @keyframes os-spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default OnboardingSponsorship;